import React, { useCallback, useState } from "react";
import { css } from "glamor";
import { Icon } from "semantic-ui-react";
import { IoChatbubblesOutline } from "react-icons/io5";
import Modal from "../../library/Modal";
import TipTapViewer from "../../library/TipTapViewer";
import { spacing } from "../../library/theme";

// ---- Feedback card (header + score + Ver comentário trigger) ----

const feedbackSectionClass = css({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
});

const feedbackIconClass = css({
  width: 32,
  height: 32,
  minWidth: 32,
  borderRadius: "50%",
  background: "#f1f5f9",
  color: "#64748b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
});

const feedbackCardClass = css({
  flex: 1,
  border: "0.66px solid #e2e8f0",
  borderRadius: 12,
  background: "#f8fafc",
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const feedbackTitleClass = css({
  fontWeight: 600,
  fontSize: 10,
  lineHeight: "15px",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: 0.4,
});

const feedbackConcludedClass = css({
  fontSize: 13,
  lineHeight: "19.5px",
  fontWeight: 500,
  color: "#475569",
});

const feedbackScoreClass = css({
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "22px",
});

const feedbackScoreSuccessClass = css({ color: "#379c5c" });
const feedbackScoreFailureClass = css({ color: "#c56262" });

const viewCommentButtonClass = css({
  alignSelf: "flex-start",
  marginTop: spacing.xs,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 8,
  border: "0.66px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "16px",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s",
  ":hover": {
    background: "#f1f5f9",
    borderColor: "#94a3b8",
  },
});

// ---- Summary (badges) ----

const summaryCardClass = css({
  border: "0.66px solid #e2e8f0",
  borderRadius: 14,
  background: "#ffffff",
  padding: "17px",
  marginTop: spacing.s,
});

const summaryTitleClass = css({
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "18px",
  color: "#0f172a",
  marginBottom: spacing.s,
});

const badgesRowClass = css({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
});

const badgeBaseClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 8px",
  borderRadius: 6,
  fontSize: 10,
  fontWeight: 600,
  lineHeight: "15px",
});

const badgeCorrectClass = css({
  border: "1px solid #bbf7d0",
  background: "#f0fdf4",
  color: "#379c5c",
});

const badgeIncorrectClass = css({
  border: "1px solid #fecaca",
  background: "#fff4f4",
  color: "#c56262",
});

// ---- Modal ----

const modalContentWrapperClass = css({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const modalSectionTitleClass = css({
  fontWeight: 600,
  fontSize: 11,
  lineHeight: "16px",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: 0.4,
});

const modalBodyClass = css({
  fontSize: 14,
  lineHeight: "22px",
  color: "#1e293b",
});

const modalEmptyClass = css({
  fontSize: 13,
  lineHeight: "20px",
  color: "#94a3b8",
  fontStyle: "italic",
});

const modalHeaderRowClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const modalHeaderTitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  color: "#0f172a",
  margin: 0,
});

const modalCloseIconClass = css({
  cursor: "pointer",
  color: "#64748b",
  ":hover": { color: "#0f172a" },
});

/**
 * ChoicesSummary
 *
 * Consolidated feedback for an interactive question after the flow is completed.
 * Shows:
 * - Feedback card (concluded + score) with a "Ver comentário" trigger that opens
 *   a modal with the question's observation/comment.
 * - A summary card listing one colored badge per answered step.
 *
 * The list of entries expected is the conversation path already filtered to the
 * answered entries (each entry: { answer: { correct }, prompt }).
 */
export default function ChoicesSummary({
  question,
  answeredEntries = [],
}) {
  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const handleOpenComment = useCallback(() => setIsCommentOpen(true), []);
  const handleCloseComment = useCallback(() => setIsCommentOpen(false), []);

  const correctCount = answeredEntries.filter(
    (e) => e.answer?.correct === true
  ).length;
  const totalCount = answeredEntries.length;
  const percentage =
    totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
  const isPassing = percentage >= 60;

  const comment =
    question?.observation ||
    question?.commentText ||
    question?.comment ||
    null;
  const hasComment = typeof comment === "string" && comment.trim().length > 0;

  return (
    <>
      <div className={feedbackSectionClass} data-testid="choices-summary-feedback">
        <div className={feedbackIconClass}>
          <IoChatbubblesOutline />
        </div>
        <div className={feedbackCardClass}>
          <div className={feedbackTitleClass}>Feedback</div>
          <div className={feedbackConcludedClass}>Questão concluída.</div>
          <div
            className={css(
              feedbackScoreClass,
              isPassing ? feedbackScoreSuccessClass : feedbackScoreFailureClass
            )}
            data-testid="choices-summary-score"
            data-correct-count={correctCount}
            data-total-count={totalCount}
            data-percentage={percentage}
            data-passing={isPassing ? "true" : "false"}
          >
            {correctCount} de {totalCount} corretas ({percentage}%)
          </div>

          {hasComment && (
            <button
              type="button"
              data-testid="choices-summary-view-comment"
              className={viewCommentButtonClass}
              onClick={handleOpenComment}
            >
              Ver comentário
            </button>
          )}
        </div>
      </div>

      <div className={summaryCardClass} data-testid="choices-summary-badges">
        <div className={summaryTitleClass}>Resumo das suas escolhas</div>
        <div className={badgesRowClass}>
          {answeredEntries.map(({ answer }, index) => {
            const isCorrect = answer?.correct === true;
            return (
              <span
                key={index}
                data-testid="choices-summary-badge"
                data-step={index + 1}
                data-correct={isCorrect ? "true" : "false"}
                className={css(
                  badgeBaseClass,
                  isCorrect ? badgeCorrectClass : badgeIncorrectClass
                )}
              >
                Etapa {index + 1} • {isCorrect ? "Correta" : "Incorreta"}
              </span>
            );
          })}
        </div>
      </div>

      <Modal
        open={isCommentOpen}
        onClose={handleCloseComment}
        size="tiny"
        closeOnDimmerClick
      >
        <Modal.Header>
          <div className={modalHeaderRowClass}>
            <h2 className={modalHeaderTitleClass}>Comentário</h2>
            <Icon
              name="close"
              data-testid="choices-summary-comment-close"
              className={modalCloseIconClass}
              onClick={handleCloseComment}
            />
          </div>
        </Modal.Header>
        <Modal.Content data-testid="choices-summary-comment-modal">
          <div className={modalContentWrapperClass}>
            <div className={modalSectionTitleClass}>
              Comentário do professor
            </div>
            {hasComment ? (
              <TipTapViewer content={comment} className={modalBodyClass} />
            ) : (
              <p className={modalEmptyClass}>
                Nenhum comentário disponível para esta questão.
              </p>
            )}
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
}
